import { useCallback, useEffect, useState } from 'react';
import { apiCall, apiCallRaw, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';

interface AuditRow {
    id: string;
    ts: string;
    userEmail: string;
    action: string;
    entityType: string;
    entityId: string;
    repoId?: string;
    metadata?: Record<string, unknown>;
}

export function AdminAuditPage() {
    const { tokenReady } = useAuth();
    const [rows, setRows] = useState<AuditRow[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await apiCall<AuditRow[]>('/manage/audit?limit=200');
            setRows(r);
            setError(null);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }, []);

    useEffect(() => {
        if (tokenReady) void load();
    }, [tokenReady, load]);

    async function exportCsv() {
        try {
            const res = await apiCallRaw('/manage/audit/export.csv');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Audit log</h1>
                    <p className="admin-page__sub">Last 200 entries. CSV export uses the last 30 days.</p>
                </div>
                <button className="btn btn--primary" onClick={() => void exportCsv()}>Export 30-day CSV</button>
            </header>
            {error && <div className="admin-page__error">{error}</div>}
            {rows === null && !error && <div className="admin-page__loading">Loading…</div>}
            {rows && rows.length === 0 && <div className="admin-page__empty">No audit entries yet.</div>}
            {rows && rows.length > 0 && (
                <table className="admin-table admin-table--audit">
                    <thead>
                        <tr>
                            <th>Time (UTC)</th>
                            <th>Actor</th>
                            <th>Action</th>
                            <th>Entity</th>
                            <th>Repo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id}>
                                <td>{r.ts.replace('T', ' ').slice(0, 19)}</td>
                                <td>{r.userEmail}</td>
                                <td>{r.action}</td>
                                <td>
                                    {r.entityType}: <code>{r.entityId}</code>
                                </td>
                                <td>{r.repoId ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
